import type { RouteLocationNormalized } from 'vue-router';

import { stringifyQuery } from 'vue-router';

import { IaAudience, iaAudience, IaPageAudience } from '~/composables/unified/useIaAudience';
import { useConfigStore } from '~/stores/config';

/**
 * The page a signed-in student has in the coach shell instead: the page's `on`, its `:param`
 * segments filled from the route, with the query and hash carried so a category or a search
 * survives the move.
 */
const studentTarget = (on: string, to: RouteLocationNormalized): string => {
  const path = on.replace(/:(\w+)/g, (_, name: string) => encodeURIComponent(String(to.params[name] ?? '')));
  const query = stringifyQuery(to.query);

  return `${path}${query ? `?${query}` : ''}${to.hash}`;
};

/**
 * The new information architecture's routing, for every page that declares `ia` in its meta: a
 * signed-in student goes to the page's place in the coach shell when it has one, and a visitor the
 * page is not for goes where the page says.
 *
 * The IA is always off on the schools product. A page that declares `unavailableOnSchools` is left
 * to `06.schools-unavailable` there, which runs after this and sends a schools student where the
 * schools product has that page (a badge to `/coach/badges`); any other page takes its `off` route.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const page = to.meta.ia;
  if (!page || (to.meta.unavailableOnSchools && useConfigStore().isSchoolsCluster)) {
    return;
  }

  // The Nuxt context is lost after the await, so it is re-bound for navigateTo.
  const nuxtApp = useNuxtApp();
  const audience = await iaAudience();

  if (audience === IaAudience.Student && page.on) {
    const target = studentTarget(page.on, to);

    return nuxtApp.runWithContext(() => navigateTo(clusterRedirectPath(target)));
  }

  const isFor = audience === IaAudience.Student || (audience === IaAudience.Guest && page.audience === IaPageAudience.All);
  if (!isFor && page.off) {
    const off = page.off;

    return nuxtApp.runWithContext(() => navigateTo(clusterRedirectPath(off)));
  }
});
